/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import clsx from 'clsx';
import * as React from 'react';

import { ExpressionError } from '@gitbook/expr';
import { useSlateCodeHighlighter } from '@gitbook/react-uikit';
import { Block, type Change, Document, Text, TextRange, Value } from '@gitbook/slate';
import {
    Editor,
    type MarkProps,
    type NodeProps,
    type Plugin,
    useIsEditorComposing,
} from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { Button, KeyboardShortcut } from '@gitbook/spine-react';
import { useEventCallback, useLiveRef, usePrevious } from '@gitbook/util-react-hooks';

import { setKeyboardShortcutsScope } from '../../../Keyboard';
import { createDocumentationURL } from '../../../utils';
import { useOnSubmitPlugin } from '../../BasicEditor';
import { useExpressionContext } from '../context';
import {
    useAutocompleteDecoration,
    WithExpressionAutocomplete,
    type WithExpressionAutocompleteProps,
} from './WithExpressionAutocomplete';

const BLOCK_BLANK_TYPE = 'blank';
const AUTOCOMPLETE_MARK_TYPE = 'autocomplete';
const ERROR_MARK_TYPE = 'expression-error';

type CreateDocumentationURLArgs = Parameters<typeof createDocumentationURL>;

export interface ExpressionEditorProps {
    /**
     * The value of the expression.
     */
    value: string;
    /**
     * Whether the editor is read-only.
     */
    readOnly?: boolean;
    /**
     * Callback to call when the value of the expression changes.
     */
    onChange: (value: string, error: ExpressionError | null) => void;
    /**
     * Callback when the editor is focused.
     */
    onFocus?: () => void;
    /**
     * Callback when the editor is blurred.
     */
    onBlur?: () => void;
    /**
     * Callback to call when the user is submitting the content by pressing enter.
     */
    onSubmit?: () => void;
    /**
     * Auto-focus the editor when mounted.
     */
    autoFocus?: boolean;
    /**
     * Auto-select the entire expression when mounting.
     */
    autoSelect?: boolean;
    /**
     * Placeholder text to show when the expression is empty.
     */
    placeholder?: string;

    /**
     * Editing mode of the editor:
     * - expression: the expression can include both dynamic & deterministic properties.
     * - deterministic-expression: the expression can *only* include deterministic (non-dynamic) properties.
     * - template: the expression is a template expression.
     */
    mode?: WithExpressionAutocompleteProps['mode'];

    /**
     * URL to the document to show in the footer of the editor.
     */
    documentationURL?: {
        path: CreateDocumentationURLArgs[0];
        host?: CreateDocumentationURLArgs[1];
    };

    /**
     * Size of the editor.
     */
    size?: 'medium' | 'large';
}

/**
 * Editor for an expression.
 */
export function ExpressionEditor(props: ExpressionEditorProps) {
    const {
        value,
        onChange: onChangeValue,
        onSubmit,
        onFocus,
        onBlur,
        readOnly = false,
        autoFocus = false,
        autoSelect = false,
        placeholder = 'Enter your expression...',
        mode = 'expression',
        documentationURL,
        size = 'medium',
    } = props;

    const [internalValue, setInternalValue] = React.useState(() => {
        return getEditorValueFromExpression(value);
    });
    const [expressionError, setExpressionError] = React.useState<ExpressionError | null>(null);
    const latestInternalValueRef = useLiveRef(internalValue);
    const { runtime } = useExpressionContext();

    const validateExpression = React.useCallback(
        (expr: string): ExpressionError | null => {
            if (expr.length === 0) {
                setExpressionError(null);
                return null;
            }

            try {
                if (mode === 'template') {
                    const { errors } = runtime.parseTemplate(expr);
                    if (errors.length > 0) {
                        const [error] = errors;
                        setExpressionError(error);
                        return error;
                    }
                    setExpressionError(null);
                    return null;
                }

                const { invalidNodes } = runtime.parse(expr);

                if (invalidNodes.length > 0) {
                    const error = new ExpressionError('Invalid expression');
                    setExpressionError(error);
                    return error;
                }

                setExpressionError(null);
                return null;
            } catch (err) {
                setExpressionError(err);
                return err;
            }
        },
        [mode, runtime]
    );

    // When the value changes, we need to update the internal value
    React.useEffect(() => {
        const currentValue = getExpressionFromEditorValue(latestInternalValueRef.current);
        validateExpression(currentValue);
        if (currentValue !== value) {
            setInternalValue(getEditorValueFromExpression(value));
        }
    }, [value, latestInternalValueRef, validateExpression]);

    // When the editor is focused/blurred, we need to call the onFocus/onBlur callback
    const onFocusCallback = useEventCallback(onFocus ?? (() => {}));
    const onBlurCallback = useEventCallback(onBlur ?? (() => {}));

    /**
     * When the editor is blurred/focused, we trigger the callbacks.
     */
    const { isBlurred, isFocused } = internalValue;
    const wasBlurred = usePrevious(isBlurred);
    const wasFocused = usePrevious(isFocused);

    /**
     * Switch to the expression editor's specific scope when it is focused to avoid
     * triggering the help modal for example when typing ? in the expression.
     */
    React.useEffect(() => {
        if (isFocused) {
            return setKeyboardShortcutsScope('expression-editor');
        }
    }, [isFocused]);

    React.useEffect(() => {
        if (isBlurred === true && wasBlurred === false) {
            onBlurCallback();
        }
    }, [wasBlurred, isBlurred, onBlurCallback]);

    React.useEffect(() => {
        if (isFocused === true && wasFocused === false) {
            onFocusCallback();
        }
    }, [wasFocused, isFocused, onFocusCallback]);

    const submitPlugin = useOnSubmitPlugin(onSubmit);

    const highlightingPlugin = useSlateCodeHighlighter(
        internalValue.document,
        BLOCK_BLANK_TYPE,
        'javascript'
    );
    const decorateValue = React.useCallback(
        (value: Value) => decorateValueWithError(value, expressionError),
        [expressionError]
    );

    const allPlugins = React.useMemo<Plugin[]>(
        () => [highlightingPlugin, submitPlugin, { renderNode, renderMark, decorateValue }],
        [decorateValue, highlightingPlugin, submitPlugin]
    );

    React.useEffect(() => {
        if (autoFocus) {
            setInternalValue((value) => {
                const change = value.change().focus();
                if (autoSelect) {
                    change.selectAll();
                }

                return change.value;
            });
        }
    }, [autoFocus, autoSelect]);

    const onChange = (change: Change) => {
        setInternalValue(change.value);

        const expressionTextValue = getExpressionFromEditorValue(change.value);
        if (expressionTextValue !== value) {
            const error = validateExpression(expressionTextValue);
            onChangeValue(expressionTextValue, error);
        }
    };

    // The editor is a text field with an autocomplete menu bound to it, so arrow
    // keys are always meaningful here: they move the caret or navigate the
    // suggestions. Stop them from bubbling to ancestors (e.g. a radio group) that
    // would otherwise hijack the key and move focus out of the editor.
    const onKeyDown = (event: React.KeyboardEvent) => {
        if (isArrowKey(event.key)) {
            event.stopPropagation();
        }
    };

    return (
        <>
            <WithExpressionAutocomplete
                value={value}
                cursorOffset={
                    TextRange.isTextRange(internalValue.selection)
                        ? internalValue.selection.focusOffset
                        : 0
                }
                isEditorFocused={isFocused}
                mode={mode}
                onInsertText={(text) => {
                    const change = internalValue.change();
                    change.insertText(text).collapseToEnd();
                    onChange(change);
                }}
            >
                <div
                    className={clsx(
                        'flex w-full',
                        size === 'medium' ? 'px-2 py-[calc(0.375rem-1px)]' : null,
                        size === 'large' ? 'px-3 py-3' : null
                    )}
                    onKeyDown={onKeyDown}
                >
                    <div className="relative flex-1">
                        <Editor
                            tabIndex={0}
                            value={internalValue}
                            onChange={onChange}
                            plugins={allPlugins}
                            spellCheck={false}
                            readOnly={readOnly}
                        >
                            <ExpressionEditorPlaceholder
                                editorValue={internalValue}
                                placeholder={placeholder}
                            />
                        </Editor>
                    </div>
                    {onSubmit ? (
                        <div>
                            <KeyboardShortcut shortcut="Enter" />
                        </div>
                    ) : null}
                </div>
            </WithExpressionAutocomplete>

            {documentationURL || expressionError ? (
                <div className="flex w-full items-center gap-2 border-t px-2 py-1 align-middle">
                    {expressionError ? (
                        <>
                            <Icon.Alert className="size-4 text-danger" />
                            <div className="flex-1 text-ui-action text-danger">
                                {expressionError.message}
                            </div>
                        </>
                    ) : null}
                    {documentationURL ? (
                        <Button
                            size="small"
                            kind="blank"
                            label="Read the docs"
                            className="ml-auto"
                            leadingIcon={Icon.HelpCircle}
                            target="_blank"
                            href={createDocumentationURL(
                                documentationURL.path,
                                documentationURL.host ?? 'docs'
                            )}
                        />
                    ) : null}
                </div>
            ) : null}
        </>
    );
}

function renderNode(props: NodeProps) {
    const { node, children, attributes } = props;
    if (node.type === BLOCK_BLANK_TYPE) {
        return (
            <div {...attributes} className="font-mono text-ui-base">
                {children}
            </div>
        );
    }
    return null;
}

function renderMark(props: MarkProps) {
    const { mark } = props;

    switch (mark.type) {
        case AUTOCOMPLETE_MARK_TYPE:
            return <AutocompleteMark {...props} />;
        case ERROR_MARK_TYPE:
            return <ExpressionErrorMark {...props} />;
    }
    return null;
}

function decorateValueWithError(value: Value, error: ExpressionError | null): TextRange[] {
    const { selection, document } = value;

    if (!TextRange.isTextRange(selection) || !selection.isCollapsed) {
        return [];
    }

    const ranges: TextRange[] = [
        TextRange.create({
            anchorKey: selection.startKey,
            anchorOffset: selection.startOffset - 1,
            focusKey: selection.startKey,
            focusOffset: selection.startOffset,
            marks: [
                {
                    type: AUTOCOMPLETE_MARK_TYPE,
                },
            ],
            isAtomic: true,
        }),
    ];

    if (!error) {
        return ranges;
    }

    const textNodes = document.getTexts().toArray();

    if (error.token) {
        const { start: tokenStart, end: tokenEnd } = error.token;

        let offset = 0;
        for (const textNode of textNodes) {
            const nodeLength = textNode.text.length;
            const nodeStart = offset;
            const nodeEnd = offset + nodeLength;

            const intersectStart = Math.max(tokenStart, nodeStart);
            const intersectEnd = Math.min(tokenEnd, nodeEnd);

            if (intersectStart < intersectEnd) {
                ranges.push(
                    TextRange.create({
                        anchorKey: textNode.key,
                        anchorOffset: intersectStart - nodeStart,
                        focusKey: textNode.key,
                        focusOffset: intersectEnd - nodeStart,
                        marks: [{ type: ERROR_MARK_TYPE }],
                        isAtomic: true,
                    })
                );
            }

            offset += nodeLength;
        }
        return ranges;
    }

    // Fall back to using location if token is nopt present on the error.
    if (error.location) {
        const { line, column } = error.location;
        const lineIndex = line - 1;

        if (lineIndex >= 0 && lineIndex < textNodes.length) {
            const textNode = textNodes[lineIndex];
            const nodeLength = textNode.text.length;
            const anchorOffset = column - 1;

            if (anchorOffset < nodeLength) {
                ranges.push(
                    TextRange.create({
                        anchorKey: textNode.key,
                        anchorOffset,
                        focusKey: textNode.key,
                        focusOffset: anchorOffset + 1,
                        marks: [{ type: ERROR_MARK_TYPE }],
                        isAtomic: true,
                    })
                );
            }
        }
    }

    return ranges;
}

function AutocompleteMark(props: MarkProps) {
    const { children, attributes } = props;
    const suggestion = useAutocompleteDecoration();

    return (
        <>
            <span {...attributes}>{children}</span>
            {suggestion ? (
                <span
                    contentEditable={false}
                    className="inline-block no-underline decoration-transparent opacity-30"
                >
                    {suggestion}
                </span>
            ) : null}
        </>
    );
}

function ExpressionErrorMark(props: MarkProps) {
    const { children, attributes } = props;

    return (
        <span
            {...attributes}
            className="underline decoration-[var(--sp-color-text-danger-default)] decoration-wavy underline-offset-2"
        >
            {children}
        </span>
    );
}

function ExpressionEditorPlaceholder(props: { editorValue: Value; placeholder: string }) {
    const { editorValue, placeholder } = props;
    const isComposing = useIsEditorComposing();
    const expression = getExpressionFromEditorValue(editorValue);

    return !expression.length && !isComposing ? (
        <div className="pointer-events-none absolute inset-0 self-center text-light">
            {placeholder}
        </div>
    ) : null;
}

function getExpressionFromEditorValue(value: Value): string {
    return value.document.getText('\n');
}

function isArrowKey(key: string) {
    return key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' || key === 'ArrowDown';
}

function getEditorValueFromExpression(expression: string) {
    return Value.create({
        document: Document.create({
            nodes: [
                Block.create({
                    type: BLOCK_BLANK_TYPE,
                    nodes: [Text.create(expression)],
                }),
            ],
        }),
    });
}
